import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const CANONICAL = "https://cargonova.ai/privacy";
const CANONICAL_ZH = "https://cargonova.ai/zh/privacy";
const CANONICAL_TH = "https://cargonova.ai/th/privacy";

type Section = { title: string; body: string[] };
type Content = {
  eyebrow: string;
  title: string;
  summary: string;
  updated: string;
  sections: Section[];
  contactTitle: string;
  contactBody: string;
  cta1: string;
  backLabel: string;
};

const contentEn: Content = {
  eyebrow: "Privacy Policy",
  title: "How CargoNova handles your data",
  summary:
    "This Privacy Policy explains how CargoNova, a product of Deep Insights, collects, uses, stores and protects information when you visit our website or use our AI-native freight forwarding platform.",
  updated: "Last updated: July 23, 2026",
  sections: [
    {
      title: "1. Information we collect",
      body: [
        "Account and contact information you provide, such as name, work email, phone number, company name and role, when you request a demo, sign up, or contact us.",
        "Business data you or your organization upload to the platform in the course of freight forwarding operations, such as inquiries, quotations, bookings, shipping documents (B/L, invoices, packing lists) and customs data.",
        "Technical information collected automatically, including IP address, device and browser type, language, referring pages, and interactions with the site (via cookies and similar technologies).",
      ],
    },
    {
      title: "2. How we use information",
      body: [
        "To deliver, operate, secure and improve the CargoNova platform and website.",
        "To run AI agents on your instructions — quoting, booking, document extraction, customs drafting, settlement and analytics.",
        "To communicate with you about demos, onboarding, product updates, service notices and support requests.",
        "To meet legal, tax, accounting and regulatory obligations.",
      ],
    },
    {
      title: "3. Legal basis and your consent",
      body: [
        "We process personal data based on your consent, the performance of a contract with you or your organization, our legitimate interests in operating and improving CargoNova, and compliance with applicable laws.",
        "You may withdraw consent at any time by contacting us. Withdrawing consent does not affect the lawfulness of processing prior to withdrawal.",
      ],
    },
    {
      title: "4. Data sharing",
      body: [
        "We do not sell your personal data. We share information only with: (a) service providers who host, monitor and support the platform under confidentiality obligations; (b) carriers, customs authorities and partners strictly to execute the transport instructions you initiate; and (c) authorities when required by law.",
        "Cross-border data transfers are performed with appropriate safeguards consistent with applicable data protection laws.",
      ],
    },
    {
      title: "5. Data security",
      body: [
        "CargoNova is built on a cloud-native architecture with encryption in transit (TLS) and encryption at rest, role-based access control, audit logging and least-privilege operations.",
        "No system is 100% secure. We continuously monitor, patch and test the platform, and we notify affected customers of material incidents as required by law.",
      ],
    },
    {
      title: "6. Data retention",
      body: [
        "We retain personal and business data only as long as necessary for the purposes described above, to provide the service, and to comply with legal, tax and accounting obligations. When data is no longer needed, we delete or anonymize it.",
      ],
    },
    {
      title: "7. Your rights",
      body: [
        "Subject to applicable law, you may request access to, correction of, deletion of, or restriction of your personal data, and object to certain processing. To exercise these rights, contact us using the details below.",
      ],
    },
    {
      title: "8. Cookies",
      body: [
        "We use strictly necessary cookies to operate the site and optional analytics cookies to understand usage. You can manage preferences through the cookie banner or your browser settings.",
      ],
    },
    {
      title: "9. Changes to this policy",
      body: [
        "We may update this Privacy Policy from time to time. Material changes will be highlighted on this page with a revised effective date.",
      ],
    },
  ],
  contactTitle: "Contact us",
  contactBody:
    "For any privacy questions or to exercise your rights, contact Deep Insights at privacy@deep-insights.ai.",
  cta1: "Book a demo",
  backLabel: "Back to Home",
};

const contentZh: Content = {
  eyebrow: "隐私政策",
  title: "CargoNova 如何处理您的数据",
  summary:
    "本隐私政策说明 CargoNova（洞隐科技 Deep Insights 旗下产品）在您访问本网站或使用 AI Native 国际货代平台时，如何收集、使用、存储与保护相关信息。",
  updated: "最后更新：2026 年 7 月 23 日",
  sections: [
    {
      title: "一、我们收集的信息",
      body: [
        "您在申请演示、注册或与我们联系时主动提供的账户与联系信息，如姓名、工作邮箱、电话、公司名称与职位。",
        "您或您所在组织在使用平台过程中上传的业务数据，包括询价、报价、订舱、运输单证（如提单、发票、装箱单）以及关务相关数据。",
        "系统自动收集的技术信息，包括 IP 地址、设备与浏览器类型、语言、来源页面，以及通过 Cookie 及类似技术记录的交互行为。",
      ],
    },
    {
      title: "二、信息的使用方式",
      body: [
        "用于提供、运营、保障与改进 CargoNova 平台与网站。",
        "按照您的指令运行 AI Agent，完成报价、订舱、单证识别、关务预填、结算与分析等工作。",
        "与您沟通演示、上线部署、产品更新、服务通知与技术支持等事宜。",
        "履行法律、税务、会计与监管要求。",
      ],
    },
    {
      title: "三、法律依据与您的同意",
      body: [
        "我们基于您的同意、与您或您所在组织之间的合同履行、运营及改进 CargoNova 的合法利益，以及适用法律的要求处理个人数据。",
        "您可以随时撤回同意。撤回同意不影响撤回前基于同意进行的处理的合法性。",
      ],
    },
    {
      title: "四、信息的共享",
      body: [
        "我们不会出售您的个人数据。我们仅在以下情况下共享信息：（a）在保密义务下为平台提供托管、监控与支持的服务提供商；（b）承运人、海关及合作方，仅用于执行您发起的运输指令；（c）法律法规要求时向监管机构披露。",
        "涉及跨境数据传输时，我们将按照适用的数据保护法律采取相应保障措施。",
      ],
    },
    {
      title: "五、数据安全",
      body: [
        "CargoNova 基于云原生架构，采用传输加密（TLS）与存储加密、基于角色的访问控制、审计日志与最小权限原则。",
        "任何系统都无法保证绝对安全。我们持续进行监控、修复与测试，并在发生重大安全事件时依照法律要求通知受影响的客户。",
      ],
    },
    {
      title: "六、数据保留",
      body: [
        "我们仅在为上述目的、提供服务及履行法律、税务与会计义务所必需的期间内保留个人与业务数据。到期后将进行删除或匿名化处理。",
      ],
    },
    {
      title: "七、您的权利",
      body: [
        "在适用法律允许的范围内，您有权请求访问、更正、删除或限制处理您的个人数据，并对特定处理提出反对。请通过下方联系方式行使上述权利。",
      ],
    },
    {
      title: "八、Cookie 使用",
      body: [
        "我们使用必要 Cookie 以保证网站正常运行，并使用可选的分析 Cookie 以理解使用情况。您可以通过 Cookie 提示栏或浏览器设置管理偏好。",
      ],
    },
    {
      title: "九、政策更新",
      body: [
        "本隐私政策可能会不时更新。重大变更将在本页面注明，并附上新的生效日期。",
      ],
    },
  ],
  contactTitle: "联系我们",
  contactBody:
    "如对隐私相关事宜有任何疑问或需要行使您的权利，请联系洞隐科技 Deep Insights：privacy@deep-insights.ai。",
  cta1: "预约演示",
  backLabel: "返回首页",
};

const contentTh: Content = {
  eyebrow: "นโยบายความเป็นส่วนตัว",
  title: "CargoNova จัดการข้อมูลของคุณอย่างไร",
  summary:
    "นโยบายความเป็นส่วนตัวนี้อธิบายวิธีที่ CargoNova (ผลิตภัณฑ์จาก Deep Insights) เก็บรวบรวม ใช้ จัดเก็บ และปกป้องข้อมูล เมื่อคุณเข้าเว็บไซต์หรือใช้แพลตฟอร์มฟอร์เวิร์ดเดอร์แบบ AI-Native ของเรา",
  updated: "อัปเดตล่าสุด: 23 กรกฎาคม 2026",
  sections: [
    {
      title: "1. ข้อมูลที่เราเก็บรวบรวม",
      body: [
        "ข้อมูลบัญชีและติดต่อที่คุณให้ เช่น ชื่อ อีเมลที่ทำงาน หมายเลขโทรศัพท์ ชื่อบริษัท และตำแหน่ง เมื่อคุณขอสาธิต ลงทะเบียน หรือติดต่อเรา",
        "ข้อมูลทางธุรกิจที่คุณหรือองค์กรของคุณอัปโหลดไปยังแพลตฟอร์มระหว่างการดำเนินการฟอร์เวิร์ดสินค้า เช่น คำขอราคา ใบเสนอราคา การจอง เอกสารการขนส่ง (B/L ใบแจ้งหนี้ Packing List) และข้อมูลศุลกากร",
        "ข้อมูลทางเทคนิคที่เก็บโดยอัตโนมัติ เช่น IP address ประเภทอุปกรณ์และเบราว์เซอร์ ภาษา หน้าที่อ้างอิง และการโต้ตอบกับเว็บไซต์ (ผ่านคุกกี้และเทคโนโลยีที่คล้ายกัน)",
      ],
    },
    {
      title: "2. เราใช้ข้อมูลอย่างไร",
      body: [
        "เพื่อให้บริการ ดำเนินการ รักษาความปลอดภัย และปรับปรุงแพลตฟอร์มและเว็บไซต์ CargoNova",
        "เพื่อให้ AI Agent ทำงานตามคำสั่งของคุณ ได้แก่ การเสนอราคา การจอง การดึงข้อมูลเอกสาร การจัดเตรียมพิธีการศุลกากร การชำระเงิน และการวิเคราะห์",
        "เพื่อสื่อสารกับคุณเกี่ยวกับการสาธิต การเริ่มต้นใช้งาน การอัปเดตผลิตภัณฑ์ ประกาศบริการ และการสนับสนุน",
        "เพื่อปฏิบัติตามข้อกำหนดทางกฎหมาย ภาษี บัญชี และกฎระเบียบ",
      ],
    },
    {
      title: "3. ฐานทางกฎหมายและความยินยอมของคุณ",
      body: [
        "เราประมวลผลข้อมูลส่วนบุคคลบนพื้นฐานของความยินยอม การปฏิบัติตามสัญญากับคุณหรือองค์กรของคุณ ผลประโยชน์โดยชอบด้วยกฎหมายในการดำเนินงานและปรับปรุง CargoNova และการปฏิบัติตามกฎหมายที่บังคับใช้",
        "คุณสามารถถอนความยินยอมได้ตลอดเวลาโดยติดต่อเรา การถอนความยินยอมไม่มีผลกระทบต่อความชอบด้วยกฎหมายของการประมวลผลก่อนหน้านั้น",
      ],
    },
    {
      title: "4. การเปิดเผยข้อมูล",
      body: [
        "เราไม่ขายข้อมูลส่วนบุคคลของคุณ เราจะแบ่งปันข้อมูลเฉพาะกับ: (ก) ผู้ให้บริการที่โฮสต์ ตรวจสอบ และสนับสนุนแพลตฟอร์มภายใต้หน้าที่รักษาความลับ; (ข) ผู้ขนส่ง ศุลกากร และพันธมิตร เพื่อดำเนินการตามคำสั่งขนส่งที่คุณเริ่มต้นเท่านั้น; และ (ค) หน่วยงานทางการเมื่อกฎหมายกำหนด",
        "การถ่ายโอนข้อมูลข้ามพรมแดนดำเนินการภายใต้มาตรการคุ้มครองที่เหมาะสมตามกฎหมายคุ้มครองข้อมูลที่บังคับใช้",
      ],
    },
    {
      title: "5. ความปลอดภัยของข้อมูล",
      body: [
        "CargoNova สร้างบนสถาปัตยกรรม cloud-native พร้อมการเข้ารหัสในการส่ง (TLS) และการเข้ารหัสที่จัดเก็บ การควบคุมการเข้าถึงตามบทบาท บันทึกการตรวจสอบ และหลักการสิทธิ์น้อยที่สุด",
        "ไม่มีระบบใดปลอดภัย 100% เราตรวจสอบ ปรับปรุง และทดสอบแพลตฟอร์มอย่างต่อเนื่อง และจะแจ้งลูกค้าที่ได้รับผลกระทบเมื่อเกิดเหตุการณ์สำคัญตามที่กฎหมายกำหนด",
      ],
    },
    {
      title: "6. การเก็บรักษาข้อมูล",
      body: [
        "เราเก็บข้อมูลส่วนบุคคลและข้อมูลธุรกิจไว้เท่าที่จำเป็นสำหรับวัตถุประสงค์ข้างต้น การให้บริการ และการปฏิบัติตามกฎหมาย ภาษี และบัญชี เมื่อไม่จำเป็นแล้ว เราจะลบหรือทำให้เป็นข้อมูลนิรนาม",
      ],
    },
    {
      title: "7. สิทธิของคุณ",
      body: [
        "ภายใต้กฎหมายที่บังคับใช้ คุณสามารถขอเข้าถึง แก้ไข ลบ หรือจำกัดการประมวลผลข้อมูลส่วนบุคคลของคุณ และคัดค้านการประมวลผลบางประเภทได้ โปรดติดต่อเราตามรายละเอียดด้านล่างเพื่อใช้สิทธิเหล่านี้",
      ],
    },
    {
      title: "8. คุกกี้",
      body: [
        "เราใช้คุกกี้ที่จำเป็นเพื่อให้เว็บไซต์ทำงานได้ และคุกกี้วิเคราะห์ที่เป็นทางเลือกเพื่อทำความเข้าใจการใช้งาน คุณสามารถจัดการการตั้งค่าผ่านแบนเนอร์คุกกี้หรือการตั้งค่าเบราว์เซอร์",
      ],
    },
    {
      title: "9. การเปลี่ยนแปลงนโยบายนี้",
      body: [
        "เราอาจปรับปรุงนโยบายความเป็นส่วนตัวนี้เป็นครั้งคราว การเปลี่ยนแปลงที่สำคัญจะระบุไว้ในหน้านี้พร้อมวันที่มีผลใหม่",
      ],
    },
  ],
  contactTitle: "ติดต่อเรา",
  contactBody:
    "หากมีคำถามเกี่ยวกับความเป็นส่วนตัวหรือประสงค์จะใช้สิทธิของคุณ โปรดติดต่อ Deep Insights ที่ privacy@deep-insights.ai",
  cta1: "จองการสาธิต",
  backLabel: "กลับสู่หน้าแรก",
};

const Privacy = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;
  const prefix = language === "en" ? "" : `/${language}`;

  const seoTitle = isZh
    ? "隐私政策 | CargoNova"
    : isTh
    ? "นโยบายความเป็นส่วนตัว | CargoNova"
    : "Privacy Policy | CargoNova";

  const seoDesc = isZh
    ? "CargoNova 隐私政策：说明我们如何收集、使用、存储与保护您的信息。"
    : isTh
    ? "นโยบายความเป็นส่วนตัวของ CargoNova อธิบายวิธีที่เราเก็บ ใช้ และปกป้องข้อมูลของคุณ"
    : "CargoNova Privacy Policy — how we collect, use, store and protect your information.";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
      />
      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-4xl pt-6 md:pt-10 pb-5 md:pb-8">
          <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
            <ShieldCheck className="w-3 h-3" />
            {c.eyebrow}
          </span>
          <h1
            className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
              isZh ? "leading-[1.5]" : "leading-[1.25]"
            } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent`}
          >
            {c.title}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-2">
            {c.summary}
          </p>
          <p className="text-xs text-muted-foreground/70">{c.updated}</p>
        </section>

        {/* Sections */}
        <section className="relative container mx-auto px-4 max-w-4xl py-4 md:py-6">
          <div className="rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur p-6 md:p-8 space-y-8">
            {c.sections.map((s) => (
              <div key={s.title}>
                <h2 className="text-base md:text-lg font-semibold text-foreground mb-3 leading-snug">
                  {s.title}
                </h2>
                <div className="space-y-2">
                  {s.body.map((p, i) => (
                    <p
                      key={i}
                      className="text-sm md:text-[15px] text-muted-foreground leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact / CTA */}
        <section className="relative container mx-auto px-4 max-w-4xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-secondary" />
              <h3 className="text-lg md:text-xl font-bold text-foreground">
                {c.contactTitle}
              </h3>
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-5 leading-relaxed">
              {c.contactBody}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.deep-insights.ai/book-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
              >
                {c.cta1}
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to={prefix || "/"}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {c.backLabel}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
